import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, RefreshCw, ArrowRight } from "lucide-react";

export default function Quiz() {
  const { courseId, moduleId } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [module, setModule] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/api/courses/${courseId}`).then(({ data }) => {
      setCourse(data);
      const m = data.modules.find((x) => x.id === moduleId);
      setModule(m);
      setAnswers({});
      setResult(null);
    });
  }, [courseId, moduleId]);

  const submit = async () => {
    if (!module) return;
    if (Object.keys(answers).length < module.quiz.questions.length) {
      toast.error("Please answer all questions");
      return;
    }
    setBusy(true);
    try {
      const ordered = module.quiz.questions.map((_, i) => answers[i] ?? -1);
      const { data } = await api.post("/api/student/quiz/submit", { course_id: course.id, module_id: module.id, answers: ordered });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const retake = () => { setAnswers({}); setResult(null); };

  if (!course || !module || !module.quiz) return <div className="py-24 text-center">Loading...</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="quiz-page">
      <Link to={`/learn/${course.id}`} className="text-sm text-[#D92D20] font-semibold">← Back to lessons</Link>
      <div className="label-eyebrow mt-3">{module.title}</div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-1">{module.quiz.title}</h1>
      <p className="text-sm text-[#4B5563] mt-2">Pass mark: {module.quiz.pass_mark}%</p>

      {result && (
        <div className={`mt-6 rounded-xl p-6 ${result.passed ? "bg-[#064E3B] text-white" : "bg-[#FEE2E2] border border-[#FCA5A5]"}`} data-testid="quiz-result">
          <div className="label-eyebrow text-white/80">{result.passed ? "Passed" : "Try again"}</div>
          <div className="font-display font-black text-4xl mt-1">{result.score}%</div>
          <p className="mt-2 text-sm">{result.passed ? "Great work! You can continue to the next module." : `You need ${result.pass_mark}% to pass.`}</p>
          <div className="mt-4 flex gap-2">
            {!result.passed && <button onClick={retake} data-testid="quiz-retake" className="btn-primary flex items-center gap-1"><RefreshCw className="w-4 h-4" /> Retake</button>}
            <Link to={`/learn/${course.id}`} className="btn-outline" data-testid="back-to-lessons">Back to lessons</Link>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-6">
        {module.quiz.questions.map((q, qi) => {
          const r = result?.results?.[qi];
          const qKey = q.id || `q-${qi}-${(q.question || "").slice(0, 32)}`;
          return (
            <div key={qKey} data-testid={`question-${qi}`} className="bg-white border border-[#E5E7EB] rounded-xl p-5">
              <div className="font-display font-bold">Q{qi+1}. {q.question}</div>
              <div className="mt-3 space-y-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[qi] === oi;
                  let cls = "border-[#E5E7EB] hover:border-[#D92D20]";
                  if (result && r) {
                    if (oi === r.correct_index) cls = "border-[#064E3B] bg-[#064E3B]/5";
                    else if (oi === r.selected && !r.is_correct) cls = "border-[#DC2626] bg-[#FEE2E2]";
                  } else if (selected) cls = "border-[#D92D20] bg-[#FECACA]/30";
                  return (
                    <button
                      key={`${qKey}-${opt}`}
                      data-testid={`q${qi}-option-${oi}`}
                      onClick={() => !result && setAnswers((a) => ({ ...a, [qi]: oi }))}
                      disabled={!!result}
                      className={`block w-full text-left border rounded-md px-3 py-2 text-sm transition-all ${cls}`}
                    >
                      <span className="font-semibold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                      {result && oi === r.correct_index && <CheckCircle className="w-4 h-4 text-[#064E3B] inline ml-2" />}
                      {result && oi === r.selected && !r.is_correct && <XCircle className="w-4 h-4 text-[#DC2626] inline ml-2" />}
                    </button>
                  );
                })}
              </div>
              {result && r?.explanation && (
                <div className="mt-3 text-sm bg-[#FAFAFA] border border-[#E5E7EB] rounded-md p-3">
                  <span className="label-eyebrow">Explanation</span>
                  <p className="mt-1 text-[#4B5563]">{r.explanation}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!result && (
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={submit} disabled={busy} data-testid="quiz-submit" className="btn-primary flex items-center gap-1">
            Submit answers <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
